import React, { useEffect, useRef, useState } from "react";
import ProductPhotoViewer from "./ProductPhotoViewer";
import "../product-detail.css";
import "../machine-photo-gallery.css";

export default function MachinePhotoGallery({
  name,
  mainImage,
  image,
}: {
  name: string;
  mainImage?: string;
  image?: {largeImage?: string; smallImage?: string}[];
}) {
  const photos = Array.from(new Set(
    [mainImage, ...(Array.isArray(image) ? image.map(photo => photo?.largeImage || photo?.smallImage) : [])]
      .filter((src): src is string => typeof src === "string" && src.trim().length > 0)
  ));
  const [selected, setSelected] = useState<string>();
  const [open, setOpen] = useState(false);
  const opener = useRef<HTMLButtonElement>(null);
  const wasOpen = useRef(false);
  useEffect(() => {
    if (!open && wasOpen.current) opener.current?.focus();
    wasOpen.current = open;
  }, [open]);
  const index = Math.max(0, selected ? photos.indexOf(selected) : 0);

  return (
    <section className="gm-photo-gallery" aria-label="Machine photos">
      <button
        ref={opener}
        type="button"
        className="gm-photo-open"
        aria-label="Enlarge machine photo"
        disabled={!photos.length}
        onClick={() => setOpen(true)}
      >
        <img
          className="gm-machine-photo"
          src={photos[index] || "/catalog/placeholder.svg"}
          alt={name}
          onError={(event) => {
            if (!event.currentTarget.src.endsWith("/catalog/placeholder.svg"))
              event.currentTarget.src = "/catalog/placeholder.svg";
          }}
        />
      </button>
      {photos.length > 0 && <p className="gm-photo-hint">Select the photo to enlarge it.</p>}
      {photos.length > 1 && (
        <>
          <div className="gm-photo-thumbnails">
            {photos.map((src, i) => (
              <button
                type="button"
                key={src}
                aria-label={`Show machine photo ${i + 1}`}
                aria-pressed={i === index}
                onClick={() => setSelected(src)}
              >
                <img src={src} alt="" loading="lazy" width="64" height="80" />
              </button>
            ))}
          </div>
          <p className="gm-photo-count" aria-live="polite">Photo {index + 1} of {photos.length}</p>
        </>
      )}
      {open && photos.length > 0 && (
        <ProductPhotoViewer
          photos={photos}
          index={index}
          name={name}
          onChange={(i) => setSelected(photos[i])}
          onClose={() => setOpen(false)}
        />
      )}
    </section>
  );
}
